import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";

const ManageEnvFeatureFlagsTable = () => {
  const colors = useColors();
  const {
    fetchEnvFeatureFlags,
    updateEnvFeatureFlags,
    addEnvFeatureFlag,
    deleteEnvFeatureFlag,
  } = useAPI();

  const [envFeatureFlags, setEnvFeatureFlags] = useState({});
  const [rows, setRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFeatureFlags, setEditedFeatureFlags] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFeatureFlagName, setNewFeatureFlagName] = useState("");
  const [newFeatureFlagValue, setNewFeatureFlagValue] = useState("No");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState(null);

  /***************************
   *  Fetch existing flags
   ***************************/
  useEffect(() => {
    const fetchFlags = async () => {
      const response = await fetchEnvFeatureFlags();
      setEnvFeatureFlags(response.data);
    };
    fetchFlags();
  }, []);

  /****************************************************************
   *  Build the grid rows when flags change
   ****************************************************************/
  useEffect(() => {
    const newRows = Object.entries(envFeatureFlags).map(
      ([feature, value], idx) => ({
        id: idx,
        feature,
        enabled: value ? "Yes" : "No",
        rawValue: value,
      })
    );
    setRows(newRows);
    setEditedFeatureFlags({ ...envFeatureFlags });
  }, [envFeatureFlags]);

  /****************************************************
   *  If editing ends, refresh from the server
   ****************************************************/
  useEffect(() => {
    if (!isEditing) {
      const fetchFlags = async () => {
        const response = await fetchEnvFeatureFlags();
        setEnvFeatureFlags(response.data);
      };
      fetchFlags();
    }
  }, [isEditing]);

  /**********************************
   *   Handlers
   **********************************/
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      await updateEnvFeatureFlags(editedFeatureFlags);
      setEnvFeatureFlags({ ...editedFeatureFlags });
      setIsEditing(false);
      Toast.success("Environment feature flags updated successfully!");
    } catch (error) {
      Toast.error("Error updating environment feature flags");
    }
  };

  const handleCancelClick = () => {
    setEditedFeatureFlags({ ...envFeatureFlags });
    setIsEditing(false);
  };

  const handleFeatureFlagChange = (feature) => (event) => {
    setEditedFeatureFlags((prev) => ({
      ...prev,
      [feature]: event.target.value === "Yes",
    }));
  };

  /**********************************
   *   Modal (Add Feature Flag)
   **********************************/
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setNewFeatureFlagName("");
    setNewFeatureFlagValue("No");
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleAddFeatureFlag = async () => {
    try {
      const flagValue = newFeatureFlagValue === "Yes";
      await addEnvFeatureFlag({
        [newFeatureFlagName]: flagValue,
      });

      // Re-fetch to see the changes in UI
      const response = await fetchEnvFeatureFlags();
      setEnvFeatureFlags(response.data);
      Toast.success("Environment feature flag added successfully!");

      handleCloseModal();
    } catch (err) {
      Toast.error(`Error adding environment feature flag: ${err}`);
    }
  };

  /**********************************
   *   Delete Feature Flag
   **********************************/
  const handleDeleteClick = (feature) => {
    setFlagToDelete(feature);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setFlagToDelete(null);
  };

  const handleDeleteFeatureFlag = async () => {
    try {
      if (!flagToDelete) return;

      await deleteEnvFeatureFlag(flagToDelete);

      // Update local state after successful deletion
      setEnvFeatureFlags((prev) => {
        const updated = { ...prev };
        delete updated[flagToDelete];
        return updated;
      });

      Toast.success("Environment feature flag deleted successfully!");
      setIsDeleteModalOpen(false);
      setFlagToDelete(null);

      // Also update the rows
      setRows(rows.filter((row) => row.feature !== flagToDelete));
    } catch (error) {
      Toast.error(`Error deleting environment feature flag: ${error.message}`);
    }
  };

  /**********************************
   *   Columns for the DataGrid
   **********************************/
  const columns = [
    { field: "feature", headerName: "Feature", width: 220 },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 350,
      renderCell: (params) => {
        return isEditing ? (
          <TextField
            select
            value={editedFeatureFlags[params.row.feature] ? "Yes" : "No"}
            onChange={handleFeatureFlagChange(params.row.feature)}
            fullWidth
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>
        ) : (
          <Chip
            label={params.value}
            size="small"
            sx={{
              color: colors.common_black,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={() => handleDeleteClick(params.row.feature)}
            size="small"
            disabled={isEditing}
          >
            <DeleteIcon
              color={isEditing ? "disabled" : "error"}
              fontSize="small"
            />
          </IconButton>
        );
      },
    },
  ];

  /**********************************
   *   Render
   **********************************/
  return (
    <Box sx={{ marginTop: "16px", width: "90%", maxWidth: "800px" }}>
      <Paper sx={{ padding: 2 }}>
        <Typography variant="h6" gutterBottom>
          Manage Environment Feature Flags
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
          {/* Edit / Save / Cancel Buttons */}
          <SecondaryButton
            onClick={isEditing ? handleSaveClick : handleEditClick}
            disabled={!rows.length}
            sx={{ marginRight: 1 }}
          >
            {isEditing ? "Save" : "Edit"}
          </SecondaryButton>

          {isEditing && (
            <SecondaryButton
              onClick={handleCancelClick}
              sx={{ marginRight: 1 }}
            >
              Cancel
            </SecondaryButton>
          )}

          {/* Add Feature Flag */}
          <SecondaryButton onClick={handleOpenModal}>
            Add Feature Flag
          </SecondaryButton>
        </Box>
      </Paper>

      {/* Data Grid */}
      <StyledDataGrid
        rows={rows}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        autoHeight
        sx={{
          "& .MuiDataGrid-cell": {
            padding: "8px",
          },
        }}
      />

      {/* Modal */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: colors.common_white,
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Add New Environment Feature Flag
          </Typography>

          <TextField
            fullWidth
            label="Feature Flag Name"
            value={newFeatureFlagName}
            onChange={(e) => setNewFeatureFlagName(e.target.value)}
            sx={{ marginTop: 2 }}
          />

          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel id="feature-flag-value-label">Value</InputLabel>
            <Select
              labelId="feature-flag-value-label"
              value={newFeatureFlagValue}
              onChange={(e) => setNewFeatureFlagValue(e.target.value)}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>

          <Box
            sx={{ marginTop: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleCloseModal}
              sx={{ marginRight: 2 }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddFeatureFlag}
              variant="contained"
              disabled={!newFeatureFlagName.trim()}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteModalOpen} onClose={handleDeleteModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: colors.common_white,
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Delete Environment Feature Flag
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete the environment feature flag{" "}
            <Typography component="span" fontWeight="bold">
              {flagToDelete}
            </Typography>
            ?
          </Typography>
          <Box
            sx={{ marginTop: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleDeleteModalClose}
              sx={{ marginRight: 2 }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteFeatureFlag}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ManageEnvFeatureFlagsTable;
